import React, { useMemo } from "react";

const Paginator = ({
  page = 1,
  totalPages = 1,
  onPage = () => {},

  pageSize = 10,
  onPageSizeChange, // keep compatible (can be undefined)
  pageSizeOptions = [5, 10, 20, 50],

  siblingCount = 1,
  boundaryCount = 1,
}) => {
  const safeTotalPages = Math.max(1, Number(totalPages) || 1);
  const safePage = Math.min(Math.max(1, Number(page) || 1), safeTotalPages);

  const go = (p) => {
    if (p < 1 || p > safeTotalPages) return;
    if (p === safePage) return;
    onPage(p);
  };

  const paginationRange = useMemo(() => {
    const total = safeTotalPages;
    const current = safePage;

    const maxButtonsWithoutDots =
      boundaryCount * 2 + siblingCount * 2 + 3;

    // small total => show all pages
    if (total <= maxButtonsWithoutDots) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const startPages = Array.from(
      { length: Math.min(boundaryCount, total) },
      (_, i) => i + 1
    );

    const endPages = Array.from(
      { length: Math.min(boundaryCount, total) },
      (_, i) => total - Math.min(boundaryCount, total) + i + 1
    );

    const startSibling = Math.max(boundaryCount + 1, current - siblingCount);
    const endSibling = Math.min(total - boundaryCount, current + siblingCount);

    const shouldShowLeftDots = startSibling > boundaryCount + 1;
    const shouldShowRightDots = endSibling < total - boundaryCount;

    const middlePages = [];
    for (let i = startSibling; i <= endSibling; i += 1) middlePages.push(i);

    const range = [];
    range.push(...startPages);

    if (shouldShowLeftDots) range.push("…");

    range.push(...middlePages);

    if (shouldShowRightDots) range.push("…");

    for (const p of endPages) {
      if (!range.includes(p)) range.push(p);
    }

    return range;
  }, [safeTotalPages, safePage, siblingCount, boundaryCount]);

  // ✅ AFTER hook: safe early return (no hook rule violation)
  const hasPageSize = typeof onPageSizeChange === "function";
  if (safeTotalPages <= 1 && !hasPageSize) return null;

  return (
    <div className="sa-pg-row">
      <div className="sa-pg-left">
        <span style={{ color: "#6b7280" }}>Show</span>

        <select
          className="sa-sel"
          value={pageSize}
          onChange={(e) => hasPageSize && onPageSizeChange(Number(e.target.value))}
          disabled={!hasPageSize}
        >
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>
              {n} / page
            </option>
          ))}
        </select>
      </div>

      <div className="sa-pg">
        <button
          className="sa-pg-btn"
          onClick={() => go(safePage - 1)}
          disabled={safePage <= 1}
        >
          Prev
        </button>

        {paginationRange.map((n, idx) => {
          if (n === "…") {
            return (
              <span
                key={`dots-${idx}`}
                className="sa-pg-dots"
                style={{
                  padding: "0 8px",
                  color: "#6b7280",
                  userSelect: "none",
                }}
              >
                …
              </span>
            );
          }

          return (
            <button
              key={n}
              className={`sa-pg-btn ${n === safePage ? "active" : ""}`}
              onClick={() => go(n)}
            >
              {n}
            </button>
          );
        })}

        <button
          className="sa-pg-btn"
          onClick={() => go(safePage + 1)}
          disabled={safePage >= safeTotalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Paginator;
